import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getShowsByVenueID } from '../../util/util';
import { groupShowsByStatus } from '../../util/data-factory';
import EditShowModal from './modals/EditShowModal';
import ShowHideModal from './modals/ShowHideModal';
import ShowCombineModal from './modals/ShowCombineModal';
import { ShowTableGroup } from './ShowTable';

const ShowsSection = forwardRef(function ShowsSection({ selectedVenueId, refreshKey = 0, tourTargetShowId }, ref) {
  const [shows, setShows] = useState(null);
  const [selectedItem, setSelectedItem] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [showToHide, setShowToHide] = useState(null);
  const [isCombineModalOpen, setIsCombineModalOpen] = useState(false);
  const [showToCombine, setShowToCombine] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setShows(null);
    setLoadError(false);
    if (!selectedVenueId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getShowsByVenueID(String(selectedVenueId));
        if (!cancelled && data) setShows(data);
      } catch (error) {
        console.error('Error fetching shows:', error);
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedVenueId, refreshKey]);

  const handleShowSaved = useCallback((show, isNew) => {
    if (isNew) {
      setShows(prev => [...(prev ?? []), show]);
    } else {
      setShows(prev => prev.map(s => s.show_id === show.show_id ? show : s));
    }
  }, []);

  const handleShowImagesSaved = useCallback((showId, imageUrls) => {
    // selectedItem is left alone: changing it while the form is open would reset unsaved edits
    setShows(prev => prev.map(s => s.show_id === showId ? { ...s, image_urls: imageUrls, locked: 1 } : s));
  }, []);

  const handleShowHidden = useCallback((showId, newHiddenValue) => {
    setShows(prev => prev.map(s => s.show_id === showId ? { ...s, is_archived: newHiddenValue, locked: 1 } : s));
  }, []);

  const handleShowsCombined = useCallback((keepShowId, mergeShowId) => {
    setShows(prev => prev.map(s => (
      s.show_id === mergeShowId ? { ...s, is_archived: true, merged_into: keepShowId, locked: 1 } : s
    )));
  }, []);

  const handleEditShow = useCallback(show => {
    setSelectedItem(show);
    setIsModalOpen(true);
  }, []);

  const handleHideShow = useCallback(show => {
    setShowToHide(show);
    setIsHideModalOpen(true);
  }, []);

  const handleCombineShow = useCallback(show => {
    setShowToCombine(show);
    setIsCombineModalOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({
    hasLoaded: () => shows !== null,
    getFirstShowForTour: () => {
      if (!shows) return null;
      const { live, past, hidden } = groupShowsByStatus(shows);
      return live[0] ?? past[0] ?? hidden[0] ?? null;
    },
    openEditModalFor: handleEditShow,
    closeShowModal: () => setIsModalOpen(false),
  }), [shows, handleEditShow]);

  if (!selectedVenueId) return null;

  if (loadError) {
    return <p className="text-slate-400 text-sm">Couldn't load shows for this venue. Please try again.</p>;
  }

  if (shows === null) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="h-8 w-24 bg-slate-200 rounded animate-pulse" />
          <div className="h-8 w-24 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="px-6 space-y-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const { live, past, hidden } = groupShowsByStatus(shows);
  const tableProps = { onEditShow: handleEditShow, onHideShow: handleHideShow, tourTargetShowId };

  const combineTargets = showToCombine
    ? shows.filter(s => !s.is_archived && s.show_id !== showToCombine.show_id)
    : [];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl text-secondary-red font-semibold tracking-wide uppercase">Shows</h2>
        <div className="flex gap-2">
          <button
            data-tour="add-show-button"
            onClick={() => handleEditShow(null)}
            className="btn btn-outline btn-sm focus:outline-none"
          >
            + Add Show
          </button>
        </div>
      </div>

      {live.length === 0 && past.length === 0 && hidden.length === 0 && (
        <p className="text-slate-400 text-sm">No shows for this venue.</p>
      )}

      <ShowTableGroup title="Live Shows" rows={live} {...tableProps} onCombineShow={handleCombineShow} />
      <ShowTableGroup title="Past Shows" rows={past} {...tableProps} onCombineShow={handleCombineShow} />
      <ShowTableGroup title="Hidden Shows" rows={hidden} {...tableProps} hideAction="unhide" />

      <EditShowModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        selectedItem={selectedItem}
        selectedVenueId={selectedVenueId}
        onShowSaved={handleShowSaved}
        onShowImagesSaved={handleShowImagesSaved}
      />
      <ShowHideModal
        isModalOpen={isHideModalOpen}
        setIsModalOpen={setIsHideModalOpen}
        selectedShow={showToHide}
        onShowHidden={handleShowHidden}
      />
      <ShowCombineModal
        isModalOpen={isCombineModalOpen}
        setIsModalOpen={setIsCombineModalOpen}
        selectedShow={showToCombine}
        combineTargets={combineTargets}
        onShowsCombined={handleShowsCombined}
      />
    </div>
  );
});

export default ShowsSection;
